import { useCallback, useState } from 'react';
import { useAppDispatch } from '@/app/hooks';
import { muteTrack } from '@/features/live/liveThunks';
import { useParticipants } from '@livekit/components-react';
import { Track } from 'livekit-client';

interface UseAdminTrackControlProps {
  roomName: string;
  participantIdentity: string;
  workspaceId: number;
  webinarId: number;
  sessionId: number;
}

export const useAdminTrackControl = ({
  roomName,
  participantIdentity,
  workspaceId,
  webinarId,
  sessionId,
}: UseAdminTrackControlProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const dispatch = useAppDispatch();
  const participants = useParticipants();

  const getTrackSid = useCallback(
    (source: Track.Source): string | null => {
      const participant = participants.find((p) => p.identity === participantIdentity);
      if (!participant) {
        console.warn(`Participant ${participantIdentity} not found`);
        return null;
      }

      const trackPublication = participant.getTrackPublication(source);
      if (!trackPublication || !trackPublication.trackSid) {
        console.warn(`Track ${source} not found for participant ${participantIdentity}`);
        return null;
      }

      return trackPublication.trackSid;
    },
    [participants, participantIdentity],
  );

  const setCameraEnabled = useCallback(
    async (enabled: boolean) => {
      if (!roomName || !participantIdentity || !workspaceId || !webinarId || !sessionId) return;

      setIsLoading(true);
      try {
        const trackSid = getTrackSid(Track.Source.Camera);
        if (!trackSid) {
          console.error('Camera trackSid not found');
          return;
        }

        await dispatch(
          muteTrack({
            workspaceId,
            webinarId,
            sessionId,
            identity: participantIdentity,
            trackSid,
            muted: !enabled,
          }),
        ).unwrap();
      } catch (error) {
        console.error('Failed to set camera state:', error);
        throw error;
      } finally {
        setIsLoading(false);
      }
    },
    [roomName, participantIdentity, workspaceId, webinarId, sessionId, dispatch, getTrackSid],
  );

  const setMicrophoneEnabled = useCallback(
    async (enabled: boolean) => {
      if (!roomName || !participantIdentity || !workspaceId || !webinarId || !sessionId) return;

      setIsLoading(true);
      try {
        const trackSid = getTrackSid(Track.Source.Microphone);
        if (!trackSid) {
          console.error('Microphone trackSid not found');
          return;
        }

        await dispatch(
          muteTrack({
            workspaceId,
            webinarId,
            sessionId,
            identity: participantIdentity,
            trackSid,
            muted: !enabled,
          }),
        ).unwrap();
      } catch (error) {
        console.error('Failed to set microphone state:', error);
        throw error;
      } finally {
        setIsLoading(false);
      }
    },
    [roomName, participantIdentity, workspaceId, webinarId, sessionId, dispatch, getTrackSid],
  );

  return {
    setCameraEnabled,
    setMicrophoneEnabled,
    isLoading,
  };
};
